import { Fragment, useEffect, useState } from 'react';
import { useRecordContext, useNotify, useRefresh, type RaRecord } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { SectionCard } from '../../shared/Panel';
import {
  addCandidate,
  assignFromSuggestion,
  rejectSuggestion,
  toErrorMessage,
  type SuggestionAssignment,
} from './orderActionsApi';
import { useHasPermission } from '../PermissionGate';
import { useVariantScope, withVariantScopeNote } from './variantScope';
import { variantRefusalOf, windowWarningOf, withWindowWarning } from './variantWindow';
import { useAssignOverride } from './AssignOverride';
import { BasisList, BasisToggle } from './SuggestionBasis';
import { fetchSuggestions, type SuggestionsResult } from './suggestionsApi';
import { OrderRouteNote } from './RouteFigureCell';
import { accessSourceNote, isMeasured } from './routeFigure';
import { plural } from '../../shared/plural';
import { otherOrdersText } from './candidateRanks';
import {
  BusyMarker,
  CALC_COLUMNS,
  CandidateCalcCells,
  CandidateCalcHeaders,
  DriverUnderVehicle,
  NOWRAP,
  VehicleModelLines,
} from './candidateCells';
import { shortDateTime } from '../../shared/dateTime';
import { recordScoped, useStillMounted } from '../../shared/recordScoped';
import { TABLE_SCROLL_SX } from '../../shared/tableScroll';
import { OnlyMineToggle, useOnlyMine } from '../fleet/myVehicles';
import { PickupGeoNote } from './geoPoint';
import { TruckBanNote } from './TruckBanNote';
import { useFeature } from '../featureFlags/FeatureGate';
import { byPairScore, PairScoreLine, PairScoreSortToggle } from './PairScore';
import { PriceStatisticsText } from './PriceStatisticsLine';
import { TeamLeaderAssignsButton, usePlannerSeesDisabledAssign } from './AssignByTeamLeader';
import { FEATURE_PRICE_STATISTICS } from './priceStatistics';
import { useNarrowLayout } from '../useNarrowLayout';
import {
  byFeasibility,
  ComputedAtNote,
  FEATURE_COLUMN_SORT,
  FEATURE_DETAILS,
  FEATURE_FEASIBILITY,
  FeasibilityChip,
  nextSort,
  SkippedReasonList,
  SortableLabel,
  sortRecommendations,
  type RecommendationSort,
  type RecommendationSortKey,
} from './recommendationView';

/**
 * Podpowiedzi pojazdów pod zlecenie.
 *
 * ## Co planista widzi i czego nie widzi
 *
 * Widzi **składniki decyzji osobno**: dolot, od kiedy pojazd jest wolny, ile
 * zostaje zapasu i co budzi wątpliwość. Od 13.10.2026 (przełącznik
 * `pairScoring`, plan scoringu) obok stoi **ocena pary 0–10** pod numerem
 * pojazdu, z rozbiciem w dymku i w „Na jakiej podstawie” (`PairScore.tsx`).
 * Kolejność z API zostaje po dolocie; „Sortuj po ocenie” przestawia tylko
 * widok (zajęte i tak na końcu). Bez przełącznika — jak dotąd, bez oceny.
 *
 * ## Dolot ma podpisane źródło
 *
 * Od 06.10.2026 (decyzja użytkownika) dolot i trasę zlecenia liczą mapy HERE
 * — trasa ciężarówki. Dotychczasowy rachunek (linia prosta razy mnożnik
 * zmierzony na 6576 trasach klienta, rozrzut około ±8%, czas z modelu floty)
 * zostaje poglądowo w dymku przy ikonce „i” obok liczby. Gdy mapy są
 * wyłączone albo nie odpowiedziały, liczbą jest szacunek — wtedy nagłówek
 * mówi „Dolot (szacunek)”, jak dotąd. Przy mieszanych źródłach nagłówek jest
 * neutralny („Dolot”), a źródło stoi pod liczbą w każdej komórce
 * (`RouteFigureCell.tsx`); pod tabelą zdanie o źródle (`routeFigure.ts`).
 *
 * ## Pozycja z GPS-u, gdy odczyt świeży; inaczej z historii zleceń
 *
 * Od 21.09 silnik bierze ostatni odczyt eco-trucka, o ile nie jest starszy
 * niż doba i auto nie jest w trakcie kursu; wtedy pozycją jest punkt
 * rozładunku ostatniego zlecenia. Źródło jest podpisane przy każdym
 * kandydacie — GPS z czasem odczytu albo numer zlecenia (od 01.10.2026 link
 * do karty zlecenia w nowej karcie, `suggestionColumns.tsx`) — bo bez tego
 * liczba kilometrów byłaby nie do sprawdzenia.
 *
 * ## Dlaczego pokazujemy pominiętych — i imiennie
 *
 * Z 1200 pojazdów zostaje kilka. Lista bez rozliczenia, co się stało
 * z resztą, wygląda jak błąd — a różnica między „nikt nie pasuje" i „dwieście
 * aut nie ma znanej pozycji" jest dla planisty zasadnicza. Żeton z powodem
 * rozwija się do numerów rejestracyjnych: „czy P1MID40 jest w tej dwusetce?"
 * to pytanie, na które sama liczba nie odpowiada.
 *
 * ## Konflikt wariantów to informacja, nie blokada
 *
 * Auto, które kandyduje już do innego zlecenia, zostaje w rankingu z dopiskiem
 * „wskazany też do zleceń: …" i nadal da się je dodać jako wariant albo nim
 * obsadzić. Decyzja, które zlecenie dostanie auto, należy do planisty — nie
 * do listy podpowiedzi.
 *
 * ## „Obsadź” jednym kliknięciem (omówienie 01.10.2026)
 *
 * Główną akcją wiersza jest „Obsadź” (uprawnienie `FTL_ASSIGN`): jedno
 * kliknięcie, bez okna potwierdzenia, robi to, co „Zmień pojazd” w akcjach
 * karty — pojazd spoza wariantów staje się wariantem, zlecenie jest nim
 * obsadzone, pozostałe warianty są zwalniane, a obsadzone dotąd auto zostaje
 * podmienione. Do TMS nic nie idzie. Obok mniejsze „Wariant” (`FTL_PLANNING`)
 * — wstępne zaplanowanie bez obsadzania. Wiersz pojazdu, który już obsadza
 * zlecenie, ma znacznik „Obsadzony” i lekko wyróżnione tło; aktywny wariant —
 * znacznik „Wariant” zamiast przycisku.
 *
 * ## „Tylko moje pojazdy” zawęża API, zanim obetnie listę
 *
 * Opiekun FTL może zawęzić listę do swoich aut (status 29.09.2026,
 * `fleet/myVehicles.tsx`). Od 02.10.2026 robi to API (`mine=1`, opiekun
 * z konta): wybiera najbliższe auta planisty z pełnej listy, a dopiero
 * potem obcina ją do dziesięciu. Wcześniej front chował cudze auta
 * w pierwszej dziesiątce całej floty i planista zwykle nie widział żadnego
 * swojego. To filtr dla wygody, nie uprawnienie: kolejność silnika zostaje
 * (po dolocie), nad listą stoi liczba ukrytych aut innych opiekunów
 * i „Pokaż wszystkie”. Przełączenie pokazuje wynik już pobrany albo pobiera
 * brakujący. Bez wskazanego opiekuna nic nie znika.
 */

/**
 * Podpis grupy pominiętych pojazdów w podsumowaniu „Nie pokazano N
 * pojazdów: zajęte w tym terminie (3), …”. Podpisy przychodzą z API
 * (`skippedLabels`, 07.10.2026: „mapa etykiet po stronie API”); klucz
 * powodu to dane i służy tylko do grupowania. Starsze API — sam klucz.
 */
export const skipGroupLabel = (result: Pick<SuggestionsResult, 'skippedLabels'>, reason: string) =>
  result.skippedLabels?.[reason] ?? reason;

/**
 * „nie starszy niż doba” — wiek odczytu GPS z parametru `maxPositionAgeHours`
 * (07.10.2026: liczby w tekstach z parametrów, nie wpisane na stałe). Przy
 * wartości domyślnej (24 h) i w starszym API — „doba”, jak dotąd.
 */
export const positionAgeText = (hours: number | undefined) =>
  hours === undefined || hours === 24 ? 'doba' : plural(hours, 'godzina', 'godziny', 'godzin');

/** „Nie pokazano 1 pojazdu” / „… 307 pojazdów” — po przeczeniu zawsze dopełniacz. */
const skippedSummaryText = (n: number) =>
  `Nie pokazano ${plural(n, 'pojazdu', 'pojazdów', 'pojazdów')}:`;

/**
 * Zdanie nad listą zawężoną do aut planisty — z liczbą aut innych opiekunów
 * w pełnej liście rekomendacji (z API, nie z pierwszej dziesiątki).
 */
const onlyMineNote = (others: number) =>
  `Pokazujemy tylko Twoje pojazdy. Ukrywamy ${plural(
    others,
    'pojazd innego opiekuna',
    'pojazdy innych opiekunów',
    'pojazdów innych opiekunów',
  )}.`;

/**
 * Klucz wyniku w pamięci panelu: cała flota albo lista jednego opiekuna.
 * Wynik jednego widoku nie trafia do drugiego, a zmiana opiekuna w menu
 * użytkownika nie podsuwa listy poprzedniego.
 */
const resultKey = (onlyMine: boolean, caretaker: string | null) =>
  onlyMine && caretaker !== null ? `mine:${caretaker}` : 'all';

/** „odrzucona: powód (kto, kiedy)” — ten sam tekst w wierszu i w dymku. */
export const rejectedText = (r: { reason: string | null; at: string; by: string | null }) =>
  `odrzucona: ${r.reason ?? '—'} (${r.by ?? '—'}, ${shortDateTime(r.at)})`;

/**
 * Szerokość linii „odrzucona: …” w kolumnie „Pojazd”. Zmierzone na dev
 * (28.09.2026, zlecenie 17762 przy 1366 px): 220 px poszerzało tabelę o 47 px,
 * 160 px mieściło się na styk — 140 px zostawia zapas.
 */
export const REJECTED_REASON_MAX_WIDTH = 140;

/**
 * Liczba kolumn tabeli — wiersze rozwinięte (podstawy, odrzucenie) zajmują
 * całą szerokość. „Zapas do okna” (15.10.2026) stoi w kolumnie „Zapasu
 * czasu pracy”, więc kolumn nie przybywa.
 */
const COLUMN_COUNT = CALC_COLUMNS + 2;

/** Przegroda przed zajętymi pojazdami na końcu listy (06.10.2026). */
export const BUSY_DIVIDER_TEXT =
  'Zajęte innym zleceniem w tym terminie (szacowana zajętość) — na końcu listy, po dolocie; obsadzenie albo wariant nadal możliwe.';

/** Oznaczenie zajętego pojazdu — wspólne z tabelą wariantów (`candidateCells.tsx`). */
export { BusyMarker };

/**
 * Węższe marginesy komórek (8 zamiast 16 px) i wyrównanie do góry: wiersz
 * kandydata ma kilka linii, a liczby wyśrodkowane w pionie rozjeżdżały się
 * względem numeru rejestracyjnego.
 */
/**
 * Odstęp w komórce 6 px zamiast 8 px poniżej 1536 px (29.10.2026, przegląd
 * wyglądu na różnych rozdzielczościach): przy 1280 px tabela potrzebowała
 * 1041 px przy 1015 px miejsca i przewijała się w bok — pierwsza kolumna
 * (pojazd) chowała się po kliknięciu „Na jakiej podstawie”. 8 kolumn × 4 px
 * daje 32 px.
 */
export const TABLE_CELL_PX = { xs: 0.75, xl: 1 } as const;

/**
 * Odstęp w komórce 4 px poniżej 1366 px (30.10.2026, decyzja Bartka: tabela
 * rekomendacji bez przewijania w bok także w wyglądzie jak dotąd): przy
 * 1024 px brakowało 4 px, przy 1280 px — 6 px; 7–8 kolumn × 4 px daje
 * 28–32 px. Od 1366 px bez zmian.
 */
export const TABLE_CELL_PX_BELOW_1366 = 0.5;
export const BELOW_1366_QUERY = '@media (max-width:1365.95px)';

const TABLE_SX = {
  '& .MuiTableCell-root': { px: TABLE_CELL_PX },
  [BELOW_1366_QUERY]: { '& .MuiTableCell-root': { px: TABLE_CELL_PX_BELOW_1366 } },
  '& .MuiTableCell-body': { verticalAlign: 'top' },
  '& .MuiTableCell-head': { verticalAlign: 'bottom' },
} as const;

/**
 * Kolumna akcji: „Obsadź”, pod nim „Wariant”, na dole ikony — w pionie,
 * wyrównane do prawej, każdy napis w jednym kawałku. W jednym wierszu
 * (0.30.0) kolumna miała ~255 px i tabela prowadzącego podział potrzebowała
 * 1279 px — przy oknie 1366–1440 px „Wariant” i ikony wypadały poza kartę
 * (02.10.2026: „widok plus wariant jest zepsuty, nie mogę dodać pojazdu”).
 * W pionie kolumna ma szerokość najszerszego przycisku, a wiersz kandydata
 * i tak ma kilka linii, więc nie rośnie.
 */
const ACTIONS_COLUMN_SX = {
  alignItems: 'flex-end',
  '& > *': { flexShrink: 0 },
} as const;
const ACTIONS_ICONS_SX = { justifyContent: 'flex-end' } as const;

/** Wiersz pojazdu, który już obsadza zlecenie — lekkie tło w kolorze „Obsadzony”. */
const ASSIGNED_ROW_BG = (theme: Theme) =>
  alpha(theme.palette.success.main, theme.palette.action.selectedOpacity);

/** Podpowiedź przy „Obsadź” — brzmienie z omówienia 01.10.2026. */
export const ASSIGN_TOOLTIP =
  'Obsadza zlecenie tym pojazdem. Pozostałe warianty zostaną zwolnione. Do TMS nic nie wysyłamy.';

/**
 * Podpowiedź przy „Obsadź”. Gdy zlecenie obsadza już inny pojazd, „Obsadź”
 * go podmienia (jak „Zmień pojazd”) — mówimy który, zanim planista kliknie.
 */
export function assignTooltip(currentPlate: string | null): string {
  return currentPlate === null
    ? ASSIGN_TOOLTIP
    : `Obsadza zlecenie tym pojazdem zamiast ${currentPlate}. Pozostałe warianty zostaną zwolnione. Do TMS nic nie wysyłamy.`;
}

/** Podpowiedź przy „Wariant” — wstępne zaplanowanie, bez obsadzania. */
export const VARIANT_TOOLTIP =
  'Dodaje pojazd jako wariant, czyli wstępnie planuje go do zlecenia. Zlecenie nie zostaje obsadzone.';

/** Ostrzeżenia po obsadzeniu zostają na ekranie dłużej — to kilka zdań do przeczytania. */
const WARNING_NOTICE_MS = 8000;

/**
 * Komunikat po „Obsadź”: zdanie o obsadzeniu i — gdy są — ostrzeżenia z API
 * (pojazd innej spółki, warianty pojazdu zwolnione w innych zleceniach).
 * Ten sam pojazd drugi raz (lista sprzed odświeżenia) to informacja, nie błąd.
 */
export function assignmentNotice(
  result: Pick<SuggestionAssignment, 'changed' | 'registrationNumber' | 'warnings'>,
  fallbackPlate: string,
): { message: string; type: 'success' | 'warning' | 'info' } {
  const plate = result.registrationNumber ?? fallbackPlate;
  if (!result.changed) {
    return { message: `Zlecenie jest już obsadzone pojazdem ${plate}.`, type: 'info' };
  }
  const warnings = Array.isArray(result.warnings) ? result.warnings : [];
  return {
    message: [`Zlecenie obsadzone pojazdem ${plate}.`, ...warnings].join(' '),
    type: warnings.length > 0 ? 'warning' : 'success',
  };
}

/** Co rekord zlecenia mówi o obsadzeniu — `availableActions` z API szczegółów. */
type AssignActions = { canAssign?: boolean; assignBlockedReason?: string | null };

/**
 * Dlaczego „Obsadź” jest teraz niedostępne (zlecenie niepotwierdzone, stan,
 * w którym nie obsadzamy) — powód z API, przycisk wyszarzony z wyjaśnieniem.
 * `null` = wolno, także gdy API tego nie podało (rozstrzygnie serwer).
 */
function assignBlockedReason(record: RaRecord): string | null {
  const actions = record.availableActions as AssignActions | undefined;
  if (actions?.canAssign !== false) return null;
  return actions.assignBlockedReason ?? 'Obsadzenie tego zlecenia jest teraz niedostępne.';
}

/** Pojazdy aktywnych wariantów zlecenia — znacznik „Wariant” zamiast przycisku. */
function activeVariantIds(record: RaRecord): Set<number> {
  const candidates = (record.candidates as RaRecord[] | undefined) ?? [];
  return new Set(candidates.filter((k) => k.status === 'active').map((k) => Number(k.carId)));
}

/**
 * Kto dodał aktywny wariant pojazdu (15.10.2026, makieta FTL Etap 1: „stan
 * wariantu: czy para jest już wariantem, czyim”) — login z rekordu karty,
 * jak w sekcji wariantów. Brak autora = brak wpisu.
 */
export function variantAuthors(record: RaRecord): Map<number, string> {
  const candidates = (record.candidates as RaRecord[] | undefined) ?? [];
  const out = new Map<number, string>();
  for (const k of candidates) {
    const by = (k.addedBy as { username?: string | null } | null | undefined)?.username;
    if (k.status === 'active' && by) out.set(Number(k.carId), by);
  }
  return out;
}

/** Pojazd obsadzający zlecenie: id i numer do podpowiedzi „zamiast …”. */
function assignedVehicle(record: RaRecord): { id: number; plate: string } | null {
  if (record.assignedCarId === null || record.assignedCarId === undefined) return null;
  const id = Number(record.assignedCarId);
  const car = record.assignedCar as { registrationNumber?: string | null } | null | undefined;
  return { id, plate: car?.registrationNumber ?? `#${id}` };
}

const VehicleSuggestionsPanel = () => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const stillMounted = useStillMounted();
  // „Obsadź mimo blokady” — Team Leader (16.10.2026, pyt. 54e).
  const override = useAssignOverride();
  // Wyniki po widoku listy (`resultKey`): cała flota i „Tylko moje pojazdy”
  // to dwa różne pytania do API (02.10.2026). Przełączenie wraca do wyniku
  // już pobranego; „Przelicz ponownie” i akcje zostawiają tylko świeży wynik
  // bieżącego widoku — inne są wtedy nieaktualne.
  const [results, setResults] = useState<Record<string, SuggestionsResult>>({});
  const [busy, setBusy] = useState(false);
  // Który powód pominięcia jest rozwinięty do listy pojazdów — jeden naraz,
  // bo „bez znanej pozycji" potrafi mieć dwieście wierszy.
  const [expandedReason, setExpandedReason] = useState<string | null>(null);
  const [showLate, setShowLate] = useState(false);
  // Odrzucenie z powodem: który pojazd i treść powodu (FTL V2.1).
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  // „Na jakiej podstawie” — rozwinięte uzasadnienie jednego auta naraz.
  const [basisFor, setBasisFor] = useState<number | null>(null);
  // Pojazd, którym właśnie obsadzamy — jego „Obsadź” pokazuje ładowanie,
  // pozostałe są w tym czasie zablokowane (`busy`).
  const [assigning, setAssigning] = useState<number | null>(null);
  // „Obsadź” to obsadzenie (`FTL_ASSIGN`), „Wariant” — planowanie
  // (`FTL_PLANNING`). Bez uprawnienia przycisku nie ma (01.10.2026).
  const canAssignVehicle = useHasPermission('FTL_ASSIGN');
  const canPlan = useHasPermission('FTL_PLANNING');
  // Planista widzi „Obsadź” nieaktywny z podpowiedzią (20.10.2026,
  // `assignButtonForPlanner`); bez przełącznika — przycisku nie ma.
  const plannerSeesDisabledAssign = usePlannerSeesDisabledAssign();
  // Planista dodaje warianty tylko przy swoich autach (07.10.2026,
  // `variantScope.ts`) — przy cudzych przycisku nie ma; API i tak odmawia.
  const canManageVariant = useVariantScope();
  // Ocena pary 0–10 (13.10.2026) — przełącznik funkcji; sortowanie po niej
  // to wybór widoku, domyślnie kolejność z API (po dolocie).
  const scoring = useFeature('pairScoring');
  const priceStatisticsOn = useFeature(FEATURE_PRICE_STATISTICS);
  const [sortByScore, setSortByScore] = useState(false);
  // Makieta FTL, Etap 1 (15.10.2026) — trzy przełączniki: wykonalność
  // z zapasem do okna, szczegóły (po zleceniu, koszt, autor wariantu,
  // pominięte z powodem, godzina przeliczenia) i sortowanie nagłówkiem.
  const feasibilityOn = useFeature(FEATURE_FEASIBILITY);
  const detailsOn = useFeature(FEATURE_DETAILS);
  const columnSortOn = useFeature(FEATURE_COLUMN_SORT);
  const [sort, setSort] = useState<RecommendationSort | null>(null);
  // Kiedy policzono wynik danego widoku (cała flota / tylko moje).
  const [computedAt, setComputedAt] = useState<Record<string, string>>({});
  // Poniżej 1280 px kierowca pod pojazdem, bez własnej kolumny (29.10.2026,
  // decyzja Bartka: „1024 px — zrób tak, żeby było dobrze i czytelnie”).
  const narrow = useNarrowLayout();
  const COLUMNS = narrow ? COLUMN_COUNT - 1 : COLUMN_COUNT;
  // Sortowanie nagłówkiem i „Sortuj po ocenie” wykluczają się — wybór
  // jednego zdejmuje drugie, żeby było jasne, według czego stoi lista.
  const onSort = (column: RecommendationSortKey) => {
    setSort((s) => nextSort(s, column));
    setSortByScore(false);
  };
  const onScoreSort = (checked: boolean) => {
    setSortByScore(checked);
    if (checked) setSort(null);
  };

  // „Tylko moje pojazdy”: włączony przełącznik (i wskazany opiekun) to
  // pytanie do API z `mine=1` — API wybiera auta planisty, zanim obetnie
  // listę do pierwszej dziesiątki. Front niczego już sam nie chowa.
  const mine = useOnlyMine('suggestions');
  const key = resultKey(mine.onlyMine, mine.caretaker);
  const suggestions = results[key] ?? null;
  // Policzone cokolwiek — dla któregokolwiek widoku.
  const computed = Object.keys(results).length > 0;

  // Odsiewamy WYŁĄCZNIE werdykty pewne, czyli oparte na danych klienta
  // (kierowca na urlopie). Limit czasu jazdy i dolot to nasze oszacowania
  // — auto z takim werdyktem zostaje na liście z etykietą, bo kierowcę przy
  // pojeździe można zmienić, a naszej arytmetyki nie podnosimy do rangi
  // wyroku. Decyzja z 22.09.2026; reguła werdyktu: `user-offers/zdazenie.ts`.
  const allCandidates = suggestions?.candidates ?? [];
  const isFilteredOut = (k: (typeof allCandidates)[number]) =>
    k.onTime?.willMakeIt === false && k.onTime.certain === true;
  const filteredOut = allCandidates.filter(isFilteredOut);
  const visible = showLate ? allCandidates : allCandidates.filter((k) => !isFilteredOut(k));
  // „Sortuj po ocenie”: wolne po ocenie, zajęte dalej na końcu (też po
  // ocenie) — przegroda przed zajętymi zostaje w jednym miejscu. Tak samo
  // sortowanie nagłówkiem i — z wykonalnością (15.10.2026) — kolejność
  // domyślna „wykonalne na górze, w grupie po dolocie”.
  const ordered = (rows: typeof visible) =>
    scoring && sortByScore
      ? byPairScore(rows)
      : columnSortOn && sort
        ? sortRecommendations(rows, sort)
        : feasibilityOn
          ? byFeasibility(rows)
          : rows;
  const shown =
    (scoring && sortByScore) || (columnSortOn && sort) || feasibilityOn
      ? [...ordered(visible.filter((k) => !k.busyWith)), ...ordered(visible.filter((k) => k.busyWith))]
      : visible;
  const authors = record ? variantAuthors(record) : new Map<number, string>();
  // Choć jeden dolot z map w tabeli: nagłówek bez „(szacunek)”, a każda
  // komórka podpisuje źródło — inaczej szacunek wyglądałby jak trasa z map.
  const measuredAccess = visible.some((k) => isMeasured(k.accessRoute));
  const accessNote = suggestions
    ? accessSourceNote(
        allCandidates.map((k) => k.accessRoute),
        [suggestions.order.route],
      )
    : null;

  // Lista zawężona przez API do aut planisty — liczba aut innych opiekunów
  // pochodzi z pełnej listy rekomendacji, nie z pierwszej dziesiątki.
  const narrowed = suggestions?.mine?.applied === true;
  const othersCount = narrowed ? (suggestions?.otherCaretakersCount ?? 0) : 0;
  // Żadnego swojego kandydata, choć auta innych opiekunów są — inne zdanie
  // niż „nie mamy żadnego pojazdu”, które byłoby nieprawdą.
  const noneOfMine = narrowed && allCandidates.length === 0 && othersCount > 0;
  // Prośba o swoje pojazdy, a na koncie nie ma opiekuna (np. wybór zmieniony
  // w innej karcie) — API oddało listę całej floty i trzeba to powiedzieć.
  const notNarrowed = suggestions?.mine?.requested === true && !narrowed;
  const showAll = () => mine.setOnlyMine(false);

  // `readOnly`: błąd samego odczytu podpowiedzi dla zlecenia, z którego
  // planista już wyszedł, nie ma czego mu powiedzieć o bieżącym — milczy.
  // Błąd zapisu (wariant, odrzucenie) mówimy zawsze: to jego akcja.
  const run = async (action: () => Promise<unknown>, readOnly = false) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      if (!readOnly || stillMounted()) notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Pobranie dla widoku z chwili wywołania — odpowiedź trafia pod jego
   * klucz, nawet gdy planista w międzyczasie przełączył widok. `replace`:
   * świeży wynik po przeliczeniu albo akcji, pozostałe widoki wypadają.
   */
  const fetchView = async (replace: boolean) => {
    if (!record) return;
    const forKey = key;
    const result = await fetchSuggestions(record.id, { mine: mine.onlyMine });
    const at = new Date().toISOString();
    setResults((prev) => (replace ? { [forKey]: result } : { ...prev, [forKey]: result }));
    setComputedAt((prev) => (replace ? { [forKey]: at } : { ...prev, [forKey]: at }));
  };

  // Przełączenie widoku po policzeniu: wynik z pamięci, a gdy go nie ma —
  // jedno pobranie (bez ponawiania w kółko po błędzie: „Przelicz ponownie”
  // jest pod ręką). Przed pierwszym policzeniem przełącznik niczego nie
  // liczy — rekomendacje są na żądanie.
  useEffect(() => {
    if (!computed || results[key] !== undefined) return;
    void run(() => fetchView(false), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, computed]);

  if (!record) return null;

  const load = () =>
    void run(async () => {
      await fetchView(true);
      setExpandedReason(null);
    }, true);

  const addVariant = (carId: number, plate: string | null) =>
    void run(async () => {
      const added: unknown = await addCandidate(record.id, carId, { origin: 'suggestion' });
      // Ostrzeżenie o oknach (21.10.2026, `variantWithinWindows`) — wariant
      // jest, ale wynik niepewny albo brak danych.
      notify(withWindowWarning(withVariantScopeNote(`Dodano wariant ${plate ?? `#${carId}`}`, added), added), {
        type: windowWarningOf(added) ? 'warning' : 'success',
      });
      refresh();
    });

  /**
   * „Obsadź” — jedno kliknięcie, bez okna potwierdzenia (omówienie
   * 01.10.2026). Po zapisie: komunikat, odświeżona karta (stan, „Obsadzone
   * pojazdem”, warianty, dziennik) i przeliczona lista rekomendacji.
   */
  const assign = (carId: number, plate: string | null) =>
    void run(async () => {
      setAssigning(carId);
      let result: SuggestionAssignment;
      try {
        result = await assignFromSuggestion(record.id, carId);
      } catch (err) {
        // Same blokady z uprawnień i konto Team Leadera — okno z powodem
        // zamiast błędu; potwierdzenie ponawia „Obsadź” (pyt. 54e).
        const offered = override.offer(err, async (reason) => {
          const overridden = await assignFromSuggestion(record.id, carId, {
            reason,
            overrideBlocking: true,
          });
          await afterAssign(overridden, carId, plate);
        });
        if (offered) return;
        throw err;
      } finally {
        setAssigning(null);
      }
      await afterAssign(result, carId, plate);
    });

  /** Po obsadzeniu: komunikat, odświeżona karta i przeliczona lista. */
  const afterAssign = async (
    result: SuggestionAssignment,
    carId: number,
    plate: string | null,
  ) => {
    const notice = assignmentNotice(result, plate ?? `#${carId}`);
    notify(
      notice.message,
      notice.type === 'warning'
        ? { type: notice.type, autoHideDuration: WARNING_NOTICE_MS }
        : { type: notice.type },
    );
    refresh();
    // Lista po obsadzeniu jest nieaktualna (warianty pojazdów w innych
    // zleceniach). Błąd przeliczenia nie przekreśla obsadzenia, które już
    // się udało i zostało ogłoszone — wtedy lista zostaje, jaka była,
    // a „Przelicz ponownie” jest pod ręką.
    try {
      await fetchView(true);
    } catch {
      // celowo bez drugiego komunikatu — patrz wyżej
    }
  };

  const reject = (carId: number, plate: string | null) =>
    void run(async () => {
      await rejectSuggestion(record.id, carId, rejectReason);
      notify(`Odrzucono rekomendację pojazdu ${plate ?? `#${carId}`}`, { type: 'success' });
      setRejecting(null);
      setRejectReason('');
      await fetchView(true);
      refresh();
    });

  // Obsadzenie i warianty z rekordu karty — po `refresh()` same się zgadzają.
  const assigned = assignedVehicle(record);
  const isAssignedCar = (carId: number) => assigned?.id === carId;
  const variants = activeVariantIds(record);
  const blockedReason = assignBlockedReason(record);

  const skippedEntries = suggestions
    ? Object.entries(suggestions.skippedSummary)
    : [];

  return (
    <SectionCard title="Rekomendowane pojazdy (wg dolotu)">
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          disabled={busy}
          onClick={load}
          // Znacznik dla „Pokaż rekomendowane pojazdy” z alertu
          // o przeplanowaniu (`vehicleSuggestionsAnchor.ts`): póki nic nie
          // policzono, alert klika ten przycisk za planistę; potem tylko przewija.
          data-suggest-start={computed ? undefined : ''}
        >
          {computed ? 'Przelicz ponownie' : 'Pokaż rekomendowane pojazdy'}
        </Button>
        {busy && <CircularProgress size={18} />}
        <OnlyMineToggle caretaker={mine.caretaker} checked={mine.onlyMine} onChange={mine.setOnlyMine} />
        {scoring && computed && <PairScoreSortToggle checked={sortByScore} onChange={onScoreSort} />}
        {detailsOn && suggestions && <ComputedAtNote at={computedAt[key] ?? null} />}
      </Stack>

      {!computed && (
        <Typography variant="body2" color="text.secondary">
          Rekomendacje wyliczamy na żądanie — dolot zależy od terminu załadunku,
          więc wynik traci aktualność, gdy zmienia się plan.
        </Typography>
      )}

      {suggestions && suggestions.candidates.length === 0 && !noneOfMine && (
        <Alert severity="info">
          Na ten termin nie mamy żadnego pojazdu do zarekomendowania. Powody są
          wymienione niżej — najczęściej to brak znanej pozycji albo zajęte
          pojazdy.
        </Alert>
      )}

      {noneOfMine && (
        <Alert
          severity="info"
          sx={{ mb: 1 }}
          data-testid="none-of-mine"
          action={
            <Button color="inherit" size="small" onClick={showAll}>
              Pokaż wszystkie
            </Button>
          }
        >
          Żaden z Twoich pojazdów nie pasuje do tego zlecenia.
        </Alert>
      )}

      {narrowed && allCandidates.length > 0 && othersCount > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary" data-testid="other-caretakers">
            {onlyMineNote(othersCount)}
          </Typography>
          <Button size="small" onClick={showAll} sx={NOWRAP}>
            Pokaż wszystkie
          </Button>
        </Stack>
      )}

      {notNarrowed && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 1 }}
          data-testid="not-narrowed"
        >
          Na Twoim koncie nie wskazano opiekuna FTL, więc pokazujemy pojazdy wszystkich
          opiekunów.
        </Typography>
      )}

      {suggestions && visible.length === 0 && filteredOut.length > 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Wszystkie pasujące pojazdy mają kierowcę nieobecnego w czasie załadunku
          (według kartoteki TMS). Włącz „Pokaż pojazdy, które nie zdążą”, aby je
          zobaczyć.
        </Alert>
      )}
      {suggestions && filteredOut.length > 0 && (
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showLate}
              onChange={(e) => setShowLate(e.target.checked)}
            />
          }
          label={`Pokaż pojazdy, które nie zdążą (${filteredOut.length})`}
        />
      )}
      {suggestions?.order.tmsOrderStatus === 'P' && (
        <Alert severity="info" sx={{ mb: 1 }}>
          To zlecenie jest już obsadzone w TMS (pojazdem albo przewoźnikiem). Lista pokazuje
          możliwe zamiany.
        </Alert>
      )}
      {/* Punkt zlecenia z geokodowania — dokładność przy dolocie (06.10.2026). */}
      <PickupGeoNote order={suggestions?.order} />
      {/* Cena klienta za km na tle historii i koszt własny pierwszego auta
          z listy (statystyka cen z VIA, 13.10.2026; przełącznik). */}
      {priceStatisticsOn && suggestions?.priceStatistics && (
        <Box sx={{ mb: 1 }} data-testid="suggestions-price-statistics">
          <PriceStatisticsText comparison={suggestions.priceStatistics} />
        </Box>
      )}
      {suggestions?.horizon?.beyond && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Załadunek za {plural(suggestions.horizon.daysAhead, 'dzień', 'dni', 'dni')}. Pozycje
          pojazdów i czas pracy kierowców są z dzisiaj, a do dnia załadunku pojazdy wykonają
          jeszcze inne zlecenia. Dolot jest więc tylko orientacyjny i nie oceniamy, czy pojazd
          zdąży. Horyzont rekomendacji to{' '}
          {plural(suggestions.horizon.horizonDays, 'dzień', 'dni', 'dni')} (przyjęte założenie).
        </Alert>
      )}
      {/* Zakazy ruchu ciężarówek w kraju punktu (19.10.2026, przełącznik
          `truckBanCalendar` — bez niego API nie niesie pola). */}
      <TruckBanNote check={suggestions?.order.truckBans} />

      {/* Trasa zlecenia nad tabelą (06.10.2026) — ta sama dla każdego
          pojazdu, więc raz, a nie w każdym wierszu przy „Rozładunku”. */}
      {suggestions && visible.length > 0 && <OrderRouteNote route={suggestions.order.route} />}

      {suggestions && visible.length > 0 && (
        // `overflowX` to tylko siatka bezpieczeństwa: kolumny są dobrane tak,
        // żeby przy 1366 px tabela mieściła się bez przewijania.
        <Box sx={TABLE_SCROLL_SX} data-testid="vehicle-suggestions-scroll">
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>
                  {narrow ? 'Pojazd i kierowca' : 'Pojazd'}
                  {/* Wykonalność stoi pod numerem — sortowanie po niej z podpisu. */}
                  {feasibilityOn && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      <SortableLabel
                        sortKey="feasibility"
                        sort={sort}
                        onSort={columnSortOn ? onSort : undefined}
                      >
                        wykonalność
                      </SortableLabel>
                    </Typography>
                  )}
                </TableCell>
                <CandidateCalcHeaders
                  driverColumn={!narrow}
                  measuredAccess={measuredAccess}
                  windowReserveColumn={feasibilityOn}
                  sort={sort}
                  onSort={columnSortOn ? onSort : undefined}
                />
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((k, index) => (
                <Fragment key={k.carId}>
                  {/* Zajęte na końcu listy (06.10.2026, omówienie z PM §3.3):
                      jeden wiersz-przegroda przed pierwszym, kolejność wolnych
                      bez zmian. Akcje zostają — zajętość to ostrzeżenie. */}
                  {k.busyWith && !shown[index - 1]?.busyWith && (
                    <TableRow data-testid="busy-divider">
                      <TableCell colSpan={COLUMNS} sx={{ bgcolor: 'action.hover', py: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {BUSY_DIVIDER_TEXT}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    hover
                    data-assigned={isAssignedCar(k.carId) ? 'true' : undefined}
                    sx={{
                      opacity: k.rejected ? 0.6 : 1,
                      ...(isAssignedCar(k.carId) ? { bgcolor: ASSIGNED_ROW_BG } : {}),
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, ...NOWRAP }}>
                        {k.registrationNumber ?? `#${k.carId}`}
                      </Typography>
                      {/* Ocena pary 0–10 pod numerem (13.10.2026) — w kolumnie
                          „Pojazd”, nie osobną kolumną: tabela ma się mieścić
                          przy 1366 px. Rozbicie w dymku. */}
                      {scoring && <PairScoreLine score={k.pairScore} />}
                      {/* Wykonalność (15.10.2026) — kategoria z werdyktów, nie ocena. */}
                      {feasibilityOn && <FeasibilityChip feasibility={k.feasibility} />}
                      {/* Jedna linia z wielokropkiem, pełna treść w dymku — w wąskiej
                          kolumnie „Pojazd” powód łamał się na siedem linii (e2e,
                          28.09.2026). Szerokość nie większa niż reszta kolumny
                          (numer, model): przy 220 px długi powód poszerzał kolumnę
                          i tabela przewijała się w karcie o 47 px (dev, 17762). */}
                      {k.rejected && (
                        <Tooltip title={rejectedText(k.rejected)}>
                          <Typography
                            variant="caption"
                            color="error.main"
                            noWrap
                            data-testid="rejected-reason"
                            sx={{ display: 'block', maxWidth: REJECTED_REASON_MAX_WIDTH }}
                          >
                            {rejectedText(k.rejected)}
                          </Typography>
                        </Tooltip>
                      )}
                      <VehicleModelLines makeAndModel={k.makeAndModel} carType={k.carType} />
                      {/* Kto dodał wariant tego auta (15.10.2026) — jedna linia
                          z wielokropkiem, jak powód odrzucenia. */}
                      {detailsOn && variants.has(k.carId) && authors.has(k.carId) && (
                        <Tooltip title={`Wariant dodał: ${authors.get(k.carId)}`}>
                          <Typography
                            variant="caption"
                            color="primary.main"
                            noWrap
                            data-testid="variant-author"
                            sx={{ display: 'block', maxWidth: REJECTED_REASON_MAX_WIDTH }}
                          >
                            {`wariant: ${authors.get(k.carId)}`}
                          </Typography>
                        </Tooltip>
                      )}
                      {k.candidateFor.length > 0 && (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ display: 'block' }}
                        >
                          {otherOrdersText(k.candidateFor)}
                        </Typography>
                      )}
                      {k.busyWith && <BusyMarker busy={k.busyWith} />}
                      {narrow && <DriverUnderVehicle candidate={k} />}
                    </TableCell>
                    {/* Wyliczenia — te same komórki co przy wariantach (06.10.2026). */}
                    <CandidateCalcCells
                      driverColumn={!narrow}
                      candidate={k}
                      showAccessSource={measuredAccess}
                      orderRoute={suggestions.order.route}
                      windowReserveColumn={feasibilityOn}
                      tightThresholdH={k.feasibility?.tightThresholdH}
                      showAfterOrder={detailsOn}
                    />
                    <TableCell align="right">
                      {/* Akcje słowem — „Obsadź” (główna) i pod nią „Wariant” —
                          oraz pomocnicze ikonami z dymkiem, w pionie. Napisy
                          się nie łamią: dawniej przyciski tekstowe rozpadały się
                          na „DODAJ / WARIANT” i „NA JAKIEJ / PODSTAWIE”. */}
                      <Stack
                        direction="column"
                        spacing={0.5}
                        useFlexGap
                        sx={ACTIONS_COLUMN_SX}
                        data-testid="suggestion-actions"
                      >
                        {isAssignedCar(k.carId) ? (
                          <Chip
                            size="small"
                            color="success"
                            icon={<CheckIcon />}
                            label="Obsadzony"
                          />
                        ) : canAssignVehicle ? (
                          // Opis w dymku, nazwa przycisku zostaje „Obsadź”.
                          // Owijka, bo zablokowany przycisk nie pokazałby
                          // dymka z powodem blokady. „Obsadź” zakłada
                          // wariant, gdy auta nie ma wśród aktywnych — przy
                          // realizacji pewnie poza oknami (21.10.2026)
                          // wyszarzony z tym samym powodem co „Wariant”.
                          <Tooltip
                            title={
                              blockedReason ??
                              (variants.has(k.carId) ? null : variantRefusalOf(k)) ??
                              assignTooltip(assigned?.plate ?? null)
                            }
                            describeChild
                            placement="top"
                          >
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                disableElevation
                                data-testid={`ftl-zlecenie-obsadz-${k.carId}`}
                                loading={assigning === k.carId}
                                disabled={
                                  busy ||
                                  blockedReason !== null ||
                                  (!variants.has(k.carId) && variantRefusalOf(k) !== null)
                                }
                                sx={NOWRAP}
                                onClick={() => assign(k.carId, k.registrationNumber)}
                              >
                                Obsadź
                              </Button>
                            </span>
                          </Tooltip>
                        ) : plannerSeesDisabledAssign ? (
                          <TeamLeaderAssignsButton />
                        ) : null}
                        {/* Wariant to wstępne zaplanowanie — przy pojeździe,
                            który już obsadza zlecenie, nie ma czego dodawać. */}
                        {canPlan && !isAssignedCar(k.carId) && variants.has(k.carId) && (
                          <Chip size="small" color="primary" variant="outlined" label="Wariant" />
                        )}
                        {canPlan &&
                          canManageVariant(k.ftlCaretaker) &&
                          !isAssignedCar(k.carId) &&
                          !variants.has(k.carId) && (
                          // Realizacja pewnie poza oknami (21.10.2026) —
                          // wyszarzony z powodem.
                          <Tooltip title={variantRefusalOf(k) ?? VARIANT_TOOLTIP} describeChild placement="top">
                            <span data-testid="variant-button-hint">
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                aria-label="Dodaj jako wariant"
                                data-testid="ftl-zlecenie-dodaj-wariant"
                                disabled={busy || variantRefusalOf(k) !== null}
                                sx={NOWRAP}
                                onClick={() => addVariant(k.carId, k.registrationNumber)}
                              >
                                Wariant
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          useFlexGap
                          sx={ACTIONS_ICONS_SX}
                          data-testid="suggestion-icons"
                        >
                          {k.basis && k.basis.length > 0 && (
                            <BasisToggle
                              open={basisFor === k.carId}
                              onToggle={() => setBasisFor((p) => (p === k.carId ? null : k.carId))}
                              testId="ftl-zlecenie-uzasadnienie"
                            />
                          )}
                          {/* Przy otwartym formularzu bez dymka: wisiał nad
                              „Anuluj”, a przy 1920 px wychodził poza kartę.
                              Po lewej, żeby nie wystawał za prawą krawędź. */}
                          <Tooltip
                            title={rejecting === k.carId ? '' : 'Odrzuć rekomendację i podaj powód'}
                            placement="left"
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label="Odrzuć"
                                disabled={busy || rejecting === k.carId}
                                onClick={() => {
                                  setRejecting(k.carId);
                                  setRejectReason('');
                                }}
                              >
                                <BlockOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {/* Powód odrzucenia w wierszu pod kandydatem — pole
                      w kolumnie akcji rozpychało całą tabelę. */}
                  {rejecting === k.carId && (
                    <TableRow>
                      <TableCell colSpan={COLUMNS} sx={{ bgcolor: 'action.hover' }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ alignItems: 'center', justifyContent: 'flex-end' }}
                        >
                          <TextField
                            size="small"
                            fullWidth={false}
                            autoFocus
                            label="Powód odrzucenia"
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            sx={{ flex: '0 1 420px' }}
                          />
                          <Button
                            size="small"
                            color="error"
                            disabled={busy || rejectReason.trim() === ''}
                            onClick={() => reject(k.carId, k.registrationNumber)}
                          >
                            Zapisz
                          </Button>
                          <Button size="small" disabled={busy} onClick={() => setRejecting(null)}>
                            Anuluj
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                  {basisFor === k.carId && k.basis && (
                    <TableRow data-testid="ftl-zlecenie-uzasadnienie-szczegoly">
                      <TableCell colSpan={COLUMNS} sx={{ bgcolor: 'action.hover' }}>
                        <BasisList
                          title={`Na jakiej podstawie rekomendujemy ${k.registrationNumber ?? `#${k.carId}`}`}
                          basis={k.basis}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {suggestions && skippedEntries.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ flexWrap: 'wrap', alignItems: 'center' }}
            useFlexGap
          >
            <Typography variant="caption" color="text.secondary">
              {skippedSummaryText(suggestions.skipped.length)}
            </Typography>
            {skippedEntries.map(([reason, count]) => (
              <Chip
                key={reason}
                size="small"
                clickable
                // Kontrakt `data-testid` paczki (S-22.10-02): „pokaż pominięte” — u nas jeden znacznik na powód.
                data-testid="ftl-zlecenie-pominiete-pokaz"
                variant={expandedReason === reason ? 'filled' : 'outlined'}
                label={`${skipGroupLabel(suggestions, reason)} (${count})`}
                onClick={() =>
                  setExpandedReason((p) => (p === reason ? null : reason))
                }
              />
            ))}
          </Stack>
          {/* Lista z powodem przy każdym pojeździe (15.10.2026, makieta FTL
              Etap 1) — powód ze szczegółem z API (`reasonText`). */}
          {expandedReason !== null && detailsOn && (
            <SkippedReasonList
              items={suggestions.skipped
                .filter((p) => p.reason === expandedReason)
                .map((p) => ({
                  key: p.carId,
                  label: p.registrationNumber ?? `#${p.carId}`,
                  reason: p.reasonText ?? skipGroupLabel(suggestions, p.reason),
                }))}
            />
          )}
          {expandedReason !== null && !detailsOn && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5 }}
            >
              {skipGroupLabel(suggestions, expandedReason)}:{' '}
              {suggestions.skipped
                .filter((p) => p.reason === expandedReason)
                .map((p) => p.registrationNumber ?? `#${p.carId}`)
                .join(', ')}
            </Typography>
          )}
        </Box>
      )}

      {suggestions && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          {accessNote ?? (
            <>
              Dolot to <strong>szacunek</strong>: odległość w linii prostej razy
              mnożnik terenowy wyliczony z dotychczasowych tras w TMS (dokładność
              ok. ±8%), a nie wyznaczona trasa.
            </>
          )}{' '}
          Pozycja pojazdu to lokalizacja
          z <strong>GPS</strong>, jeśli odczyt jest nie starszy niż{' '}
          {positionAgeText(suggestions.maxPositionAgeHours)},
          a pojazd nie jest w trakcie zlecenia. W przeciwnym razie przyjmujemy
          miejsce rozładunku ostatniego zlecenia z{' '}
          <strong>historii zleceń</strong> z ostatnich{' '}
          {suggestions.positionWindowDays} dni. Pojazdu bez żadnego z tych źródeł
          nie pokazujemy — nie przypisujemy mu pozycji na domysł.
          {suggestions.driverHoursEnabled === false && (
            <>
              {' '}
              Uwzględnianie czasu pracy kierowców jest na razie{' '}
              <strong>wyłączone</strong>, więc „zdąży” i „dowiezie” szacujemy
              dla wypoczętego kierowcy (przerwy na długiej trasie uwzględniamy).
              Urlopy i nieobecności z kartoteki TMS nadal bierzemy pod uwagę.
            </>
          )}
        </Typography>
      )}
      {override.dialog}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const VehicleSuggestions = recordScoped(VehicleSuggestionsPanel);
